import React, { Component } from "react"
import { connect } from "react-redux"
import { bindActionCreators } from "redux"

import { actions as aiCostsActions, selectors as aiCostsSelectors } from "../../modules/aiCostsModule"

// components
import Navbar from "../../components/layout/DashboardNavbar"
import MainSection from "../../components/layout/MuiMainSection"
import ScrollableContentCard, { Padding } from "../../components/layout/ScrollableContentCard"
import Footer from "../../components/Footer"
import { formatDate_toLocal } from "../../modules/utilities"

import {
  Button, FormControlLabel, Paper, Radio, RadioGroup, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Typography,
} from "@mui/material"

const AGGREGATION = {
  ALL: "all",
  DAY: "day",
  THIRTY_DAYS: "30days",
}

const formatGbp = value =>
  value === null || value === undefined ? "-" : `£${value.toFixed(4)}`

// Monitoring row Date and news-summary group Date (see dayKey below) are both "yy-MM-dd", so they
// line up when combined in the Total costs table, and both sort correctly with a plain string compare
const byDateDescending = (a, b) => b.date.localeCompare(a.date)
const byPublicationDateDescending = (a, b) => new Date(b.publicationDate || 0) - new Date(a.publicationDate || 0)

const sumBy = (rows, key) => rows.reduce((total, row) => total + (row[key] || 0), 0)

// A day's/period's rows normally share one configured model, but show "(multiple)" rather than
// silently picking one if a model change mid-run ever left them differing
const distinctModel = rows => {
  const models = [...new Set(rows.map(row => row.model).filter(Boolean))]
  return models.length === 0 ? "-" : models.length === 1 ? models[0] : "(multiple)"
}

// Local calendar day (not UTC) a publication date falls on, as "yy-MM-dd" - the same format as
// monitoring rows' own Date, so a release's day lines up with a monitoring day in the Total costs table
const dayKey = date => {
  const d = new Date(date)
  return `${String(d.getFullYear()).slice(-2)}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
}

const groupByDate = (rows, dateOf) => {
  const byDate = {}
  rows.forEach(row => {
    const date = dateOf(row)
    byDate[date] = byDate[date] || []
    byDate[date].push(row)
  })
  return Object.entries(byDate).map(([date, dateRows]) => ({ date, rows: dateRows }))
}

// Combines a date's (or the whole period's) monitoring and news-summary rows into one Total costs row
const computeTotalsRow = (monitoringRows, newsRows) => {
  const analysis = sumBy(monitoringRows, "analysis")
  const synopsis = sumBy(monitoringRows, "synopsis")
  const totalMonitoring = analysis + synopsis
  const newsSummary = sumBy(newsRows, "summaryCost")
  return { analysis, synopsis, totalMonitoring, newsSummary, dayTotal: totalMonitoring + newsSummary }
}

class AiCosts extends Component {

  state = {
    aggregation: AGGREGATION.ALL,
  }

  renderMonitoringTable(monitoringCosts) {
    const { aggregation } = this.state

    if (aggregation === AGGREGATION.ALL) {
      return (
        <React.Fragment>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>User</TableCell>
              <TableCell align="right">Documents</TableCell>
              <TableCell>Model</TableCell>
              <TableCell align="right">Analysis</TableCell>
              <TableCell align="right">Synopsis</TableCell>
              <TableCell align="right">Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {monitoringCosts.map((row, i) => (
              <TableRow key={`${row.userId}-${row.date}-${i}`}>
                <TableCell>{row.date}</TableCell>
                <TableCell>{row.userEmail || row.userId}</TableCell>
                <TableCell align="right">{row.documents}</TableCell>
                <TableCell>{row.model}</TableCell>
                <TableCell align="right">{formatGbp(row.analysis)}</TableCell>
                <TableCell align="right">{formatGbp(row.synopsis)}</TableCell>
                <TableCell align="right">{formatGbp(row.total)}</TableCell>
              </TableRow>
            ))}
            {this.renderEmptyRow(monitoringCosts, 7)}
          </TableBody>
        </React.Fragment>
      )
    }

    const groups = aggregation === AGGREGATION.DAY
      ? groupByDate(monitoringCosts, row => row.date).sort(byDateDescending)
      : [{ date: "Last 30 days", rows: monitoringCosts }]

    return (
      <React.Fragment>
        <TableHead>
          <TableRow>
            <TableCell>{aggregation === AGGREGATION.DAY ? "Date" : "Period"}</TableCell>
            <TableCell align="right">Documents</TableCell>
            <TableCell>Model</TableCell>
            <TableCell align="right">Analysis</TableCell>
            <TableCell align="right">Synopsis</TableCell>
            <TableCell align="right">Total</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {groups.map(({ date, rows }) => (
            <TableRow key={date}>
              <TableCell>{date}</TableCell>
              <TableCell align="right">{sumBy(rows, "documents")}</TableCell>
              <TableCell>{distinctModel(rows)}</TableCell>
              <TableCell align="right">{formatGbp(sumBy(rows, "analysis"))}</TableCell>
              <TableCell align="right">{formatGbp(sumBy(rows, "synopsis"))}</TableCell>
              <TableCell align="right">{formatGbp(sumBy(rows, "total"))}</TableCell>
            </TableRow>
          ))}
          {this.renderEmptyRow(monitoringCosts, 6)}
        </TableBody>
      </React.Fragment>
    )
  }

  renderNewsSummaryTable(newsSummaryCosts) {
    const { aggregation } = this.state

    if (aggregation === AGGREGATION.ALL) {
      return (
        <React.Fragment>
          <TableHead>
            <TableRow>
              <TableCell>Publication date</TableCell>
              <TableCell>Company</TableCell>
              <TableCell>Headline</TableCell>
              <TableCell>Model</TableCell>
              <TableCell align="right">Cost</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {newsSummaryCosts.map(row => (
              <TableRow key={row.id}>
                <TableCell>{formatDate_toLocal(row.publicationDate)}</TableCell>
                <TableCell>{row.company}</TableCell>
                <TableCell>{row.headline}</TableCell>
                <TableCell>{row.model}</TableCell>
                <TableCell align="right">{formatGbp(row.summaryCost)}</TableCell>
              </TableRow>
            ))}
            {this.renderEmptyRow(newsSummaryCosts, 5)}
          </TableBody>
        </React.Fragment>
      )
    }

    const groups = aggregation === AGGREGATION.DAY
      ? groupByDate(newsSummaryCosts, row => row.publicationDate ? dayKey(row.publicationDate) : "Unknown").sort(byDateDescending)
      : [{ date: "Last 30 days", rows: newsSummaryCosts }]

    return (
      <React.Fragment>
        <TableHead>
          <TableRow>
            <TableCell>{aggregation === AGGREGATION.DAY ? "Date" : "Period"}</TableCell>
            <TableCell align="right">Releases</TableCell>
            <TableCell>Model</TableCell>
            <TableCell align="right">Total cost</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {groups.map(({ date, rows }) => (
            <TableRow key={date}>
              <TableCell>{date}</TableCell>
              <TableCell align="right">{rows.length}</TableCell>
              <TableCell>{distinctModel(rows)}</TableCell>
              <TableCell align="right">{formatGbp(sumBy(rows, "summaryCost"))}</TableCell>
            </TableRow>
          ))}
          {this.renderEmptyRow(newsSummaryCosts, 4)}
        </TableBody>
      </React.Fragment>
    )
  }

  // Only shown for Day/30 Days (see render) - combines the Monitoring and News summary tables into
  // one overview, per day or for the whole period
  renderTotalCostsTable(monitoringCosts, newsSummaryCosts) {
    const { aggregation } = this.state

    const rows = aggregation === AGGREGATION.DAY
      ? (() => {
        const monitoringByDate = {}
        groupByDate(monitoringCosts, row => row.date).forEach(g => { monitoringByDate[g.date] = g.rows })
        const newsByDate = {}
        groupByDate(newsSummaryCosts, row => row.publicationDate ? dayKey(row.publicationDate) : "Unknown").forEach(g => { newsByDate[g.date] = g.rows })

        const dates = [...new Set([...Object.keys(monitoringByDate), ...Object.keys(newsByDate)])].sort((a, b) => b.localeCompare(a))

        return dates.map(date => ({
          date,
          ...computeTotalsRow(monitoringByDate[date] || [], newsByDate[date] || []),
        }))
      })()
      : [{ date: "Last 30 days", ...computeTotalsRow(monitoringCosts, newsSummaryCosts) }]

    return (
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Typography variant="h6" sx={{ p: 2 }}>Total costs</Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{aggregation === AGGREGATION.DAY ? "Date" : "Period"}</TableCell>
              <TableCell align="right">Analysis</TableCell>
              <TableCell align="right">Synopsis</TableCell>
              <TableCell align="right">Total Monitoring</TableCell>
              <TableCell align="right">News Summary</TableCell>
              <TableCell align="right">Day Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(row => (
              <TableRow key={row.date}>
                <TableCell>{row.date}</TableCell>
                <TableCell align="right">{formatGbp(row.analysis)}</TableCell>
                <TableCell align="right">{formatGbp(row.synopsis)}</TableCell>
                <TableCell align="right">{formatGbp(row.totalMonitoring)}</TableCell>
                <TableCell align="right">{formatGbp(row.newsSummary)}</TableCell>
                <TableCell align="right">{formatGbp(row.dayTotal)}</TableCell>
              </TableRow>
            ))}
            {this.renderEmptyRow([...monitoringCosts, ...newsSummaryCosts], 6)}
          </TableBody>
        </Table>
      </TableContainer>
    )
  }

  renderEmptyRow(sourceRows, colSpan) {
    const { collected } = this.props
    return collected && sourceRows.length === 0 &&
      <TableRow><TableCell colSpan={colSpan}>No costs in the last 31 days.</TableCell></TableRow>
  }

  render() {
    const { aiCosts, portrait, urlSource } = this.props
    const { aggregation } = this.state
    const monitoringCosts = [...this.props.monitoringCosts].sort(byDateDescending)
    const newsSummaryCosts = [...this.props.newsSummaryCosts].sort(byPublicationDateDescending)

    return (
      <React.Fragment>
        <Navbar title="AI Costs" backLink portrait={portrait} />

        <MainSection portrait={true}>
          <ScrollableContentCard limitedWidth>
            <Padding>
              <div style={{ display: "flex", alignItems: "center", gap: 24, marginBottom: 16 }}>
                <Button variant="contained" onClick={() => aiCosts.collect()}>
                  Collect Costs
                </Button>

                <RadioGroup
                  row
                  value={aggregation}
                  onChange={e => this.setState({ aggregation: e.target.value })}
                >
                  <FormControlLabel value={AGGREGATION.ALL} control={<Radio />} label="All" />
                  <FormControlLabel value={AGGREGATION.DAY} control={<Radio />} label="Day" />
                  <FormControlLabel value={AGGREGATION.THIRTY_DAYS} control={<Radio />} label="30 Days" />
                </RadioGroup>
              </div>

              {aggregation !== AGGREGATION.ALL && this.renderTotalCostsTable(monitoringCosts, newsSummaryCosts)}

              <TableContainer component={Paper} sx={{ mt: 2 }}>
                <Typography variant="h6" sx={{ p: 2 }}>Monitoring costs</Typography>
                <Table size="small">
                  {this.renderMonitoringTable(monitoringCosts)}
                </Table>
              </TableContainer>

              <TableContainer component={Paper} sx={{ mt: 4 }}>
                <Typography variant="h6" sx={{ p: 2 }}>News summary costs</Typography>
                <Table size="small">
                  {this.renderNewsSummaryTable(newsSummaryCosts)}
                </Table>
              </TableContainer>
            </Padding>
          </ScrollableContentCard>
        </MainSection>

        <Footer urlSource={urlSource} />
      </React.Fragment>
    )
  }
}

const mapStateToProps = ({ aiCosts, common, user }) => {
  return {
    collected: aiCostsSelectors.getCollected(aiCosts),
    monitoringCosts: aiCostsSelectors.getMonitoringCosts(aiCosts),
    newsSummaryCosts: aiCostsSelectors.getNewsSummaryCosts(aiCosts),
    portrait: user.portrait,
    urlSource: common.urlSource,
  }
}

const mapDispatchToProps = dispatch => {
  return {
    aiCosts: bindActionCreators(aiCostsActions, dispatch),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(AiCosts)
